import { AxiosError } from 'axios';
import DatePicker from 'components/base/DatePicker';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import useBranchList from 'pages/apps/hooks/useGetBranchList';

export enum DiscountType {
  Percentage = 'Percentage',
  FixedAmount = 'FixedAmount'
}

export interface DiscountDetailsForm {
  discountName: string;
  description: string;
  discountAmount: number;
  discountPercentage: number;
  type: DiscountType;
  minimumProductAmount: number;
  minimumInvoiceAmount: number;
  maximumDiscountAmount: number;
  usageLimit: number;
  startDate: string | Date;
  endDate: string | Date;
  branchId: number | null;
  isAfterSalesDiscount: boolean;
  isForAllCustomers: boolean;
  numberOfDaysBeforeActive: number;
  isForMembers: boolean;
}

type CreateDiscountFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const CreateDiscountForm = forwardRef<CreateDiscountFormHandle>((_, ref) => {
  const [discountDetails, setDiscountDetails] = useState<DiscountDetailsForm>({
    discountName: '',
    description: '',
    discountAmount: 0,
    discountPercentage: 0,
    type: DiscountType.Percentage,
    minimumProductAmount: 0,
    minimumInvoiceAmount: 0,
    maximumDiscountAmount: 0,
    usageLimit: 0,
    startDate: new Date(),
    endDate: new Date(),
    branchId: null,
    isAfterSalesDiscount: true,
    isForAllCustomers: true,
    numberOfDaysBeforeActive: 0,
    isForMembers: false
  });
  const branchList = useBranchList();

  const handleFormChange = <K extends keyof DiscountDetailsForm>(
    field: K,
    value: DiscountDetailsForm[K]
  ) => {
    setDiscountDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const CreateDiscountFormSchema = yup.object().shape({
    discountName: yup.string().required('Discount name is required'),
    description: yup.string().required('Description is required'),
    discountAmount: yup
      .number()
      .nullable()
      .when('type', {
        is: (type: string) => type === 'FixedAmount',
        then: schema =>
          schema.min(1, 'Discount amount must be greater than 0').required(),
        otherwise: schema => schema.optional()
      }),
    discountPercentage: yup
      .number()
      .nullable()
      .when('type', {
        is: (type: string) => type === 'Percentage',
        then: schema =>
          schema
            .min(1, 'Discount percentage must be greater than 0')
            .required(),
        otherwise: schema => schema.optional()
      }),
    minimumProductAmount: yup
      .number()
      .min(1, 'Minimum product amount must be greater than 0')
      .required(),
    branchId: yup.number().required('Branch is required')
  });

  const handleSubmit = async () => {
    const formattedDiscountDetails = {
      ...discountDetails,
      startDate: formatToUTCISO(discountDetails.startDate),
      endDate: formatToUTCISO(discountDetails.endDate)
    };
    try {
      await CreateDiscountFormSchema.validate(formattedDiscountDetails, {
        abortEarly: false
      });
      await axiosInstance.post(`/api/v1/discounts`, formattedDiscountDetails);
      toast.success('Discount Created', { ...toastConfig, autoClose: 2000 });
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError)
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <Form className="mb-3">
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Discount Name</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.discountName}
            onChange={e => handleFormChange('discountName', e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>description</Form.Label>
          <Form.Control
            as="textarea"
            rows={1}
            value={discountDetails.description}
            onChange={e => handleFormChange('description', e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Discount Type</Form.Label>
          <Form.Control
            as="select"
            value={discountDetails.type}
            onChange={e =>
              handleFormChange('type', e.target.value as DiscountType)
            }
          >
            <option value="Percentage">Percentage</option>
            <option value="FixedAmount">Fixed Amount</option>
          </Form.Control>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Discount Amount</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.discountAmount ?? ''}
            onChange={e => handleFormChange('discountAmount', +e.target.value)}
            disabled={discountDetails.type === 'Percentage'}
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Discount Percentage</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.discountPercentage ?? ''}
            onChange={e =>
              handleFormChange('discountPercentage', +e.target.value)
            }
            disabled={discountDetails.type === 'FixedAmount'}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Minimum Product Amount</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.minimumProductAmount}
            onChange={e =>
              handleFormChange('minimumProductAmount', +e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Minimum Invoice Amount</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.minimumInvoiceAmount}
            onChange={e =>
              handleFormChange('minimumInvoiceAmount', +e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Maximum Discount Amount</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.maximumDiscountAmount}
            onChange={e =>
              handleFormChange('maximumDiscountAmount', +e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Usage Limit</Form.Label>
          <Form.Control
            type="text"
            value={discountDetails.usageLimit}
            onChange={e => handleFormChange('usageLimit', +e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Start Date</Form.Label>
          <DatePicker
            value={discountDetails.startDate}
            onChange={date =>
              handleFormChange('startDate', (date as Date[])[0])
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>End Date</Form.Label>
          <DatePicker
            value={discountDetails.endDate}
            onChange={date => handleFormChange('endDate', (date as Date[])[0])}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Branch</Form.Label>
          <Form.Control
            as="select"
            value={discountDetails.branchId ?? ''} // needs to be changed if the backendis ready
            onChange={e => handleFormChange('branchId', +e.target.value)}
          >
            <option value="">Select Branch</option>
            {branchList.map(branch => (
              <option value={branch.branchId} key={branch.branchId}>
                {branch.branchName}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Is After Sales Discount</Form.Label>
          <Form.Control
            as="select"
            value={discountDetails.isAfterSalesDiscount.toString()}
            onChange={e =>
              handleFormChange(
                'isAfterSalesDiscount',
                e.target.value === 'true'
              )
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Control>
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Is For All Customers</Form.Label>
          <Form.Control
            as="select"
            value={discountDetails.isForAllCustomers.toString()}
            onChange={e =>
              handleFormChange('isForAllCustomers', e.target.value === 'true')
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Control>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Number of Days Before Active</Form.Label>
          <Form.Control
            type="number"
            value={discountDetails.numberOfDaysBeforeActive}
            onChange={e =>
              handleFormChange('numberOfDaysBeforeActive', +e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Is For Members</Form.Label>
          <Form.Control
            as="select"
            value={discountDetails.isForMembers.toString()}
            onChange={e =>
              handleFormChange('isForMembers', e.target.value === 'true')
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Control>
        </Form.Group>
      </Row>
    </Form>
  );
});

export default CreateDiscountForm;
